// src/UI/Pages/CreateProductForm.tsx
import React, { Fragment, useEffect, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import styles from "./Forms.module.css";

import TextInput from "../UILibrary/TextInput";
import Submission from "../UILibrary/Submission";
import Upload from "../UILibrary/Upload";

import { defaultValuesProduct } from "../../../Utils/Global/globalConstants";
import Close from "../../../Utils/Assets/SVGs/Close";

import useImageUploadRules from "../../../Logic/Functions/Hooks/useImageUploadRules";
import useProductFormLogic from "../../../Logic/Functions/Hooks/useProductFormLogic";

import {
  CreateProductFormProps,
  ICreateProductProps,
} from "@model/Category.model";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { UnauthorizedPopupStoreImplementation } from "Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { useNavigate } from "react-router-dom";
import { ProductsService } from "../../../Logic/ProductsService";

const NAME_MAX = 120;
const SHORT_DESC_MAX = 200;
const LONG_DESC_MAX = 500;
const MAX_IMAGES = 5;
const MAX_IMAGE_MB = 5;
const MAX_FILES = 5;
const MAX_PDF_MB = 10;

const CreateProductForm: React.FC<CreateProductFormProps> = ({
  categoryId,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<ICreateProductProps>({
    mode: "onSubmit",
    defaultValues: {
      ...defaultValuesProduct,
      categoryId: categoryId || defaultValuesProduct.categoryId,
    },
  });

  const [apiErrorMessage, setApiErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const authStore = UserAuthenticationStoreImplementation();

  const productsService = new ProductsService();
  const nameValue = useWatch({ control, name: "name" }) ?? "";
  const shortDescValue = useWatch({ control, name: "shortDescription" }) ?? "";
  const longDescValue = useWatch({ control, name: "longDescription" }) ?? "";

  const onAuthError = useForceLogoutAndRedirect();
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const navigate = useNavigate();

  useEffect(() => {
    setValue("categoryId", categoryId);
  }, [categoryId, setValue]);

  const { specs, addSpec, removeSpecAt, updateSpec, resetSpecs } =
    useProductFormLogic<ICreateProductProps>({
      categories: [],
      setValue,
      specsFieldName: "specs",
      initialSpecs: [{ label: "", value: "" }],
    });

  const {
    imageRules,
    helperText: imagesHelperText,
    allowedTypes,
  } = useImageUploadRules({
    maxFiles: MAX_IMAGES,
    maxSizeMB: MAX_IMAGE_MB,
    allowedTypes: ["image/png", "image/jpeg", "image/jpg", "image/webp"],
    requireAtLeastOne: true,
    enforceTotalSize: true,
  });

  const {
    filesRules: pdfRules,
    helperText: pdfHelperText,
    allowedTypes: pdfAllowedTypes,
  } = useImageUploadRules({
    mode: "file",
    maxFiles: 1,
    maxSizeMB: MAX_PDF_MB,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  const onValidForm = async (values: ICreateProductProps) => {
    await productsService.handleCreateProduct({
      data: values,
      categoryId,
      token: authStore.authenticationState?.token ?? "",
      setLoading: setIsCreating,
      setApiError: (msg) => setApiErrorMessage(msg ?? undefined),
      setSuccessMsg: (msg) => setSuccessMessage(msg),
      onResetUI: () => {
        reset({ ...defaultValuesProduct, categoryId });
        resetSpecs();
        setValue("images", null);
        setValue("files", null);
      },
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate("/", { replace: true });
      },
    });
  };

  const onInvalidForm = () => {
    productsService.handleInvalidCreateProductForm({
      setApiError: (msg: string | null) => setApiErrorMessage(msg ?? undefined),
      setSuccessMsg: (msg: string | null) => setSuccessMessage(msg),
    });
  };

  const onCancel = () => {
    productsService.handleCancelCreateProduct({
      onResetUI: () => {
        reset({ ...defaultValuesProduct, categoryId });
        resetSpecs();
        setValue("images", null);
        setValue("files", null);
      },
      setApiError: (msg: string | null) => setApiErrorMessage(msg ?? undefined),
      setSuccessMsg: (msg: string | null) => setSuccessMessage(msg),
    });
  };

  return (
    <div className={styles["cd-pageWrapper"]} data-testid="cp-page">
      <div className={styles["cd-containerNarrow"]} data-testid="cp-container">
        <div className={styles["cd-card"]} data-testid="cp-card">
          <section
            className={`${styles["cd-cardSection"]} ${styles["cd-bgGlass"]}`}
          >
            <div className={styles["cd-cardHeader"]} data-testid="cp-header">
              <div>
                <h2 className={styles["cd-cardTitle"]} data-testid="cp-title">
                  New Product
                </h2>
                <p className={styles["cd-cardSub"]} data-testid="cp-subtitle">
                  Fill in details below
                </p>
              </div>
            </div>
          </section>

          <form
            className={styles["cd-cardBody"]}
            onSubmit={handleSubmit(onValidForm, onInvalidForm)}
            data-testid="cp-form"
          >
            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-category"
            >
              <label className={styles["cd-label"]} htmlFor="cp-category">
                Category
              </label>

              <Controller
                name="categoryId"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <input
                      type="hidden"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                    <div data-testid="cp-category-fixed">
                      Category ID:{" "}
                      <strong>{categoryId || "Missing categoryId"}</strong>
                    </div>

                    {fieldState.error?.message && (
                      <div
                        className={styles.formErrorText}
                        data-testid="cp-category-error"
                      >
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-name"
            >
              <div className={styles["cd-sectionTop"]}>
                <label className={styles["cd-label"]} htmlFor="cp-name">
                  Name
                </label>
                <span
                  className={styles["cd-counter"]}
                  data-testid="cp-name-counter"
                >
                  {nameValue.length}/{NAME_MAX}
                </span>
              </div>

              <Controller
                name="name"
                control={control}
                rules={{
                  required: "Name is required",
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= NAME_MAX ||
                    `Name must be at most ${NAME_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    label=""
                    placeholder="Enter product name"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    data-testid="cp-name-input"
                  />
                )}
              />
            </section>

            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-price-stock"
            >
              <div className={styles["cd-grid2"]}>
                <div data-testid="cp-price-field">
                  <label className={styles["cd-label"]} htmlFor="cp-price">
                    Price
                  </label>

                  <Controller
                    name="price"
                    control={control}
                    rules={{
                      required: "Price is required",
                      validate: (v) => {
                        const n = Number(v);
                        if (!Number.isFinite(n))
                          return "Price must be a number";
                        if (n <= 0) return "Price must be > 0";
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextInput
                        label=""
                        type="number"
                        placeholder="e.g., 49.99"
                        value={field.value as any}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                        data-testid="cp-price-input"
                      />
                    )}
                  />
                </div>

                <div data-testid="cp-stock-field">
                  <label className={styles["cd-label"]} htmlFor="cp-stock">
                    Stock
                  </label>

                  <Controller
                    name="stock"
                    control={control}
                    rules={{
                      required: "Stock is required",
                      validate: (v) => {
                        const n = Math.trunc(Number(v));
                        if (!Number.isFinite(n))
                          return "Stock must be a number";
                        if (n < 0) return "Stock cannot be negative";
                        if (!Number.isInteger(n))
                          return "Stock must be an integer";
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextInput
                        label=""
                        type="number"
                        placeholder="e.g., 100"
                        value={field.value as any}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                        data-testid="cp-stock-input"
                      />
                    )}
                  />
                </div>
              </div>
            </section>

            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-shortdesc"
            >
              <div className={styles["cd-sectionTop"]}>
                <label className={styles["cd-label"]} htmlFor="cp-shortdesc">
                  Short description
                </label>
                <span
                  className={styles["cd-counter"]}
                  data-testid="cp-shortdesc-counter"
                >
                  {shortDescValue.length}/{SHORT_DESC_MAX}
                </span>
              </div>

              <Controller
                name="shortDescription"
                control={control}
                rules={{
                  required: "Short description is required",
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= SHORT_DESC_MAX ||
                    `Must be at most ${SHORT_DESC_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <textarea
                      id="cp-shortdesc"
                      className={styles["cd-textarea"]}
                      rows={4}
                      placeholder="A quick product summary…"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-testid="cp-shortdesc-input"
                    />
                    {fieldState.error?.message && (
                      <div
                        className={styles.formErrorText}
                        data-testid="cp-shortdesc-error"
                      >
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-longdesc"
            >
              <div className={styles["cd-sectionTop"]}>
                <label className={styles["cd-label"]} htmlFor="cp-longdesc">
                  Long description
                </label>
                <span
                  className={styles["cd-counter"]}
                  data-testid="cp-longdesc-counter"
                >
                  {longDescValue.length}/{LONG_DESC_MAX}
                </span>
              </div>

              <Controller
                name="longDescription"
                control={control}
                rules={{
                  required: "Long description is required",
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= LONG_DESC_MAX ||
                    `Must be at most ${LONG_DESC_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <textarea
                      id="cp-longdesc"
                      className={styles["cd-textarea"]}
                      rows={6}
                      placeholder="Detailed info, features, materials, care, warranty…"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-testid="cp-longdesc-input"
                    />
                    {fieldState.error?.message && (
                      <div
                        className={styles.formErrorText}
                        data-testid="cp-longdesc-error"
                      >
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-specs"
            >
              <div className={styles["cd-sectionTop"]}>
                <label className={styles["cd-label"]}>Specs</label>
                <button
                  type="button"
                  className={styles["cd-btnGhost"]}
                  onClick={addSpec}
                  data-testid="cp-specs-add"
                >
                  Add spec
                </button>
              </div>

              <ul className={styles["cd-list"]} data-testid="cp-specs-list">
                {specs.map((s, idx) => (
                  <li key={idx} className={styles["cd-listItem"]}>
                    <Controller
                      name={`specs.${idx}.label`}
                      control={control}
                      rules={{
                        required: "Label is required",
                        validate: (v) => {
                          const t = (v ?? "").trim();
                          if (!t) return "Label is required";
                          if (t.length > 60) return "Label ≤ 60 chars";
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label=""
                          placeholder="Label (e.g., Material)"
                          value={s.label}
                          onChange={(v) => {
                            updateSpec(idx, { label: v });
                            field.onChange(v);
                          }}
                          error={fieldState.error?.message}
                          data-testid={`cp-specs-label-${idx}`}
                        />
                      )}
                    />

                    <Controller
                      name={`specs.${idx}.value`}
                      control={control}
                      rules={{
                        required: "Value is required",
                        validate: (v) => {
                          const t = (v ?? "").trim();
                          if (!t) return "Value is required";
                          if (t.length > 120) return "Value ≤ 120 chars";
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextInput
                          label=""
                          placeholder="Value (e.g., 100% Cotton)"
                          value={s.value}
                          onChange={(v) => {
                            updateSpec(idx, { value: v });
                            field.onChange(v);
                          }}
                          error={fieldState.error?.message}
                          data-testid={`cp-specs-value-${idx}`}
                        />
                      )}
                    />

                    <div>
                      <button
                        type="button"
                        className={styles["cd-removeBtn"]}
                        onClick={() => removeSpecAt(idx)}
                        aria-label="Remove spec"
                        data-testid={`cp-specs-remove-${idx}`}
                        title="Remove"
                      >
                        <Close
                          className={styles["cd-removeIcon"]}
                          width={18}
                          height={18}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-images"
            >
              <div className={styles["cd-sectionTop"]}>
                <label className={styles["cd-label"]}>Images</label>
                <span
                  className={styles["cd-counter"]}
                  data-testid="cp-images-hint"
                >
                  Up to {MAX_IMAGES} images • ≤ {MAX_IMAGE_MB}MB each
                </span>
              </div>

              <Controller
                name="images"
                control={control}
                rules={imageRules}
                render={({ field, fieldState }) => (
                  <Upload
                    value={field.value}
                    onChange={field.onChange}
                    required
                    mimeTypes={allowedTypes}
                    maxFiles={MAX_IMAGES}
                    maxSizeMB={MAX_IMAGE_MB}
                    showInlineError={false}
                    helperText={imagesHelperText}
                    error={fieldState.error?.message || null}
                    data-testid="cp-images"
                    parentTestId="cp-images"
                  />
                )}
              />
            </section>

            <section
              className={styles["cd-cardSection"]}
              data-testid="cp-section-techsheet"
            >
              <div className={styles["cd-sectionTop"]}>
                <label className={styles["cd-label"]}>
                Files (PDF)
                </label>
                <span
                  className={styles["cd-counter"]}
                  data-testid="cp-techsheet-hint"
                >
                  Optional • PDF only • ≤ {MAX_PDF_MB}MB
                </span>
              </div>

              <Controller
                name="files"
                control={control}
                rules={{ validate: (fl) => pdfRules.validate(fl) }}
                render={({ field, fieldState }) => (
                  <Upload
                    value={field.value ?? null}
                    onChange={field.onChange}
                    required={false}
                    mimeTypes={pdfAllowedTypes}
                    maxFiles={MAX_FILES}
                    maxSizeMB={MAX_PDF_MB}
                    showInlineError={false}
                    helperText={pdfHelperText}
                    error={fieldState.error?.message || null}
                    data-testid="cp-techsheet"
                    parentTestId="cp-files"
                    requiresLabel={true}
                  />
                )}
              />
            </section>

            {successMessage && (
              <div
                className={styles["cd-successBanner"]}
                data-testid="cp-success"
              >
                {successMessage}
              </div>
            )}

            {apiErrorMessage && (
              <div className={styles.formErrorText} data-testid="cp-api-error">
                {apiErrorMessage}
              </div>
            )}

            <div className={styles["cd-cardFooter"]} data-testid="cp-footer">
              <div className={styles["cd-actionsRow"]} data-testid="cp-actions">
                <button
                  type="button"
                  className={styles["cd-btnGhost"]}
                  onClick={onCancel}
                  data-testid="cp-cancel"
                >
                  Cancel
                </button>

                <Submission
                  disabled={isSubmitting || isCreating}
                  buttonText={
                    isSubmitting || isCreating
                      ? "Creating..."
                      : "Create Product"
                  }
                  className={styles["cd-btn"]}
                  data-testid="cp-submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateProductForm;
